/**
 * PDF splitting utilities for multi-document PDFs.
 * Analyses page content to detect document boundaries and splits accordingly.
 * Uses pdf-parse (already installed) for text extraction.
 *
 * NOTE: We cannot create new PDF files without a PDF generation library.
 * Instead, we return text content and page ranges — the API endpoint
 * handles storage.
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export interface SplitResult {
  pageNumber: number;
  text: string;
  suggestedType?: string;
  suggestedFilename?: string;
}


// ─── Boundary detection patterns ────────────────────────────────────────────

interface BoundaryRule {
  pattern: RegExp;
  tipo: string;
  weight: number;
}

const BOUNDARY_RULES: BoundaryRule[] = [
  { pattern: /FACTURA\s*(N[ºo°]|NUM|NÚMERO)/i, tipo: 'factura', weight: 3 },
  { pattern: /INVOICE\s*(N[ºo°]|NO|NUMBER)/i, tipo: 'factura', weight: 3 },
  { pattern: /N[ºo°]\s*(?:de\s+)?factura\s*[:\s]\s*\w+/i, tipo: 'factura', weight: 2 },
  { pattern: /PRESUPUESTO\s*(N[ºo°]|NUM)/i, tipo: 'presupuesto', weight: 3 },
  { pattern: /ALBAR[ÁA]N\s*(N[ºo°]|NUM|DE ENTREGA)/i, tipo: 'albaran', weight: 3 },
  { pattern: /N[ÓO]MINA/i, tipo: 'nomina', weight: 3 },
  { pattern: /RECIBO\s*(DE\s+SALARIO|N[ºo°])/i, tipo: 'nomina', weight: 2 },
  { pattern: /EXTRACTO\s*(BANCARIO|DE\s+CUENTA)/i, tipo: 'extracto_bancario', weight: 3 },
  { pattern: /CONTRATO\s*(DE\s+TRABAJO|MERCANTIL|DE\s+SERVICIO)/i, tipo: 'contrato', weight: 3 },
];

const NIF_REGEX = /\b[A-Z]\d{7}[A-Z0-9]\b|\b\d{8}[A-Z]\b/g;
const TOTAL_REGEX = /TOTAL\s*[:\s]*[\d.,]+\s*€?/i;
const DATE_REGEX = /\b\d{1,2}[/\-.]\d{1,2}[/\-.]\d{2,4}\b/;

/** Minimum boundary score for a page to be considered a new document start. */
const BOUNDARY_THRESHOLD = 2;

// ─── Internal helpers ───────────────────────────────────────────────────────

/**
 * Extract NIFs found on a given page text.
 * Resets the global regex state after each call.
 */
function extractNIFs(text: string): string[] {
  const matches = text.match(NIF_REGEX) ?? [];
  NIF_REGEX.lastIndex = 0;
  return matches;
}

/**
 * Attempt to detect the document type from a page's text content
 * using the same boundary rules used for split detection.
 */
function detectPageType(text: string): string | undefined {
  for (const rule of BOUNDARY_RULES) {
    if (rule.pattern.test(text)) {
      return rule.tipo;
    }
  }
  return undefined;
}

/**
 * Extract per-page text from a PDF buffer using pdf-parse v2.
 * Returns an array of strings, one per page.
 */
async function extractPagesText(pdfBuffer: Buffer): Promise<string[]> {
  const { PDFParse } = await import('pdf-parse');
  const pdf = new PDFParse({ data: pdfBuffer });
  const textResult = await pdf.getText();
  await pdf.destroy();

  // pdf-parse v2 provides per-page text via textResult.pages
  if (textResult.pages.length > 0) {
    return textResult.pages.map((p) => p.text);
  }

  // Fallback: split full text by form-feed if pages array is empty
  const fullText = textResult.text || '';
  const rawPages = fullText.split(/\f/);
  return rawPages.length > 1 ? rawPages : [fullText];
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Split a multi-page PDF into individual page results.
 * Each result includes the page number, extracted text, and an optional
 * suggested document type based on content heuristics.
 */
export async function splitPdfByPages(pdfBuffer: Buffer): Promise<SplitResult[]> {
  const pages = await extractPagesText(pdfBuffer);

  return pages.map((text, i) => ({
    pageNumber: i + 1,
    text: text.trim(),
    suggestedType: detectPageType(text),
    suggestedFilename: `page_${String(i + 1).padStart(3, '0')}.txt`,
  }));
}

/**
 * Detect document boundaries within a multi-page PDF.
 *
 * Heuristics:
 *  - Document header patterns (Factura Nº, Nómina, Extracto, etc.)
 *  - A new NIF appearing that wasn't on the previous page
 *  - The previous page ending with a TOTAL line
 *
 * @returns Array of page-number groups. E.g. `[[1,2], [3], [4,5,6]]`
 *          means three documents spanning pages 1-2, 3, and 4-5-6.
 */
export async function detectDocumentBoundaries(pdfBuffer: Buffer): Promise<number[][]> {
  const pages = await extractPagesText(pdfBuffer);

  if (pages.length <= 1) {
    return [pages.length === 1 ? [1] : []];
  }

  const groups: number[][] = [];
  let currentGroup: number[] = [1]; // first page always starts a group
  let prevNIFs = new Set(extractNIFs(pages[0]));

  for (let i = 1; i < pages.length; i++) {
    const pageNum = i + 1; // 1-based
    const text = pages[i];
    const prevText = pages[i - 1];
    let boundaryScore = 0;

    // Check for document header patterns
    for (const rule of BOUNDARY_RULES) {
      if (rule.pattern.test(text)) {
        boundaryScore += rule.weight;
        break; // take the strongest match only
      }
    }

    // Check for new NIF appearing
    const currentNIFs = new Set(extractNIFs(text));
    if (currentNIFs.size > 0) {
      const hasNewNIF = [...currentNIFs].some((nif) => !prevNIFs.has(nif));
      if (hasNewNIF) {
        boundaryScore += 1;
      }
    }

    // Check if previous page had a TOTAL line (end-of-document marker)
    if (TOTAL_REGEX.test(prevText)) {
      boundaryScore += 1;
    }

    // Check for date on current page (supporting indicator)
    if (DATE_REGEX.test(text) && boundaryScore > 0) {
      boundaryScore += 0.5;
    }

    if (boundaryScore >= BOUNDARY_THRESHOLD) {
      groups.push(currentGroup);
      currentGroup = [pageNum];
    } else {
      currentGroup.push(pageNum);
    }

    prevNIFs = currentNIFs.size > 0 ? currentNIFs : prevNIFs;
  }

  // Push the final group
  groups.push(currentGroup);

  return groups;
}

/**
 * Split a multi-page PDF into logical documents using boundary detection.
 *
 * Uses {@link detectDocumentBoundaries} to group pages, then concatenates
 * the text of each group and suggests a document type.
 */
export async function splitPdfByDocuments(
  pdfBuffer: Buffer
): Promise<{ pages: number[]; text: string; suggestedType?: string }[]> {
  const [pageGroups, allPages] = await Promise.all([
    detectDocumentBoundaries(pdfBuffer),
    extractPagesText(pdfBuffer),
  ]);

  return pageGroups.map((pages) => {
    const combinedText = pages
      .map((p) => allPages[p - 1] ?? '') // p is 1-based, array is 0-based
      .join('\n\n')
      .trim();

    return {
      pages,
      text: combinedText,
      suggestedType: detectPageType(combinedText),
    };
  });
}
